import Tooltip from 'admin/components/atoms/Tooltip';
import { components } from 'admin/schema/docs/api/admin';
import { formatNum, formatUUID } from 'admin/utils';
import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Table from '../../../atoms/Table';

type CombinedPayment = components['schemas']['CombinedPayment'];
type Leg = components['schemas']['CombinedPaymentTransaction'];

type Props = {
  combinedPayment?: CombinedPayment | null;
};

const Title = styled.div`
  font-size: 1.3rem;
  font-weight: bold;
  margin-top: 40px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Summary = styled.div`
  margin-top: 4px;
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const Total = styled.span`
  font-size: 1.3rem;
  font-weight: bold;
`;

const Note = styled.span`
  color: #757575;
  font-size: 14px;
`;

/** ベースマネー由来のレグにだけ付ける。主レグには何も付けない (要件 4.0.1) */
const FallbackChip = styled.span`
  display: inline-block;
  padding: 2px 8px;
  font-size: 12px;
  line-height: 1.25;
  border-radius: 3px;
  border: 1px solid #ddd;
  color: #757575;
  white-space: nowrap;
`;

/** 寸法は取引種別のバッジに合わせる (font-size / line-height は継承のまま) */
const CancelledBadge = styled.span`
  display: inline-block;
  padding: 0.35em 0.6em;
  text-align: center;
  border-radius: 6px;
  background-color: gray;
  color: white;
  white-space: nowrap;
`;

const MoneyCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const Current = styled.span`
  color: #757575;
`;

const HEADERS = [
  { key: 'order', label: '充当順', size: 1, align: 'center' as const },
  { key: 'money', label: 'マネー', size: 5, align: 'left' as const },
  { key: 'amount', label: '金額', size: 2, align: 'right' as const },
  { key: 'status', label: '状態', size: 1.5, align: 'left' as const },
  { key: 'transaction', label: '取引', size: 2.5, align: 'left' as const },
];

/** 既存の DetailTable / HistoryTable の「総額」と同じ算出 (マネー + キャンペーン付与を除くポイント) */
const legAmount = (leg: Leg) => (leg.money_amount ?? 0) + (leg.raw_point_amount ?? 0);

const CombinedPaymentTable = ({ combinedPayment }: Props) => {
  const legs = combinedPayment?.transactions ?? [];
  if (legs.length === 0) return null;

  return (
    <div>
      <Title>
        <span>併用払い</span>
        <Tooltip
          id="helpCombinedPayment"
          text="主マネーの残高が不足したため、ベースマネーの残高と合わせて 1 回の支払いが成立した取引です。どれか 1 つを取り消すと、対になる取引もまとめて取り消されます。"
        />
      </Title>
      <Summary>
        <span>お客様のお支払い合計</span>
        <Total>¥{formatNum(combinedPayment?.total_amount ?? 0)}</Total>
        <Note>{legs.length}つのマネーに分割されています</Note>
      </Summary>

      <Table styles={{ marginTop: '12px' }}>
        <Table.Header>
          <Table.Row noBorder styles={{ backgroundColor: '#eee' }}>
            {HEADERS.map((header) => (
              <Table.Cell
                key={header.key}
                size={header.size}
                align={header.align}
                styles={{ fontWeight: 'bold' }}
              >
                {header.label}
              </Table.Cell>
            ))}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {legs.map((leg, index) => (
            <Table.Row key={leg.id} styles={leg.is_current ? { backgroundColor: '#f5f5f5' } : {}}>
              <Table.Cell align="center" styles={{ color: '#757575' }}>
                {index + 1}
              </Table.Cell>
              <Table.Cell wordBreak="break-word">
                <MoneyCell>
                  <span>{leg.private_money_name}</span>
                  {!leg.is_primary && <FallbackChip>不足分を充当</FallbackChip>}
                </MoneyCell>
              </Table.Cell>
              <Table.Cell align="right">¥{formatNum(legAmount(leg))}</Table.Cell>
              <Table.Cell>{leg.is_modified && <CancelledBadge>取消済み</CancelledBadge>}</Table.Cell>
              <Table.Cell>
                {leg.is_current ? (
                  <Current>表示中</Current>
                ) : (
                  // 遷移先の顧客リンクは URL の private_money_id に依存しているので、
                  // そのレグ自身のマネー ID を必ず載せる
                  <Link
                    to={`/transactions/${leg.id}?private_money_id=${leg.private_money_id}`}
                  >{`(${formatUUID(leg.id)})`}</Link>
                )}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </div>
  );
};

export default CombinedPaymentTable;
